import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Select from '../../../../components/ui/Select';
import { UserSkillView, ProficiencyLevel } from '../../../../models/employee';

interface SkillsSectionProps {
  skills: UserSkillView[];
  userRole: 'employee' | 'manager';
  onSkillClick: (skill: UserSkillView) => void;
  onUpdateProficiency: (skillId: number, newProficiency: ProficiencyLevel) => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({ skills, userRole, onSkillClick, onUpdateProficiency }) => {
  const { t } = useTranslation();

  const proficiencyOptions = [
    { value: 'Awareness', label: t('Awareness') },
    { value: 'Working', label: t('Working') },
    { value: 'Strong', label: t('Strong') }
  ];

  const getProficiencyColor = (proficiency: string) => {
    switch (proficiency) {
      case 'Strong':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case 'Working':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'Awareness':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('Skills')}</h3>
        <p className="text-sm text-muted-foreground">{skills?.length} {skills?.length !== 1 ? t('skills') : t('skill')}</p>
      </div>
      <div className="space-y-4">
        {skills?.map((skill) => (
          <div
            key={skill?.id}
            className="border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => onSkillClick(skill)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-base font-semibold text-foreground">{skill?.skillName}</h4>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {skill?.reportedByName && (
                    <span className="flex items-center">
                      <Icon name="User" size={12} className="me-1" />
                      {t('Reported by')} {skill.reportedByName}
                    </span>
                  )}
                  <span className="flex items-center">
                    <Icon name="Calendar" size={12} className="me-1" />
                    {formatDate(skill?.createdOn)}
                  </span>
                </div>
              </div>

              {/* Proficiency Badge/Selector */}
              <div onClick={(e) => e?.stopPropagation()}>
                {userRole === 'manager' ? (
                  <Select
                    options={proficiencyOptions}
                    value={skill?.proficiency}
                    onChange={(value) => onUpdateProficiency(skill?.id, value as ProficiencyLevel)}
                    className="w-32"
                  />
                ) : (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getProficiencyColor(skill?.proficiency)}`}>
                    {skill?.proficiency || t('Not Set')}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {skills?.length === 0 && (
          <div className="text-center py-12 bg-muted rounded-lg border border-border">
            <Icon name="Award" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-foreground font-medium mb-1">{t('No skills added yet')}</p>
            <p className="text-sm text-muted-foreground">{t('Add skills to track capabilities')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsSection;
